/**
 * Pro notation rules (Discord / forum hand history style).
 *
 * Cards
 * - Single card: rank + suit letter — As, Kh, Td, 2c (not symbols in pro text).
 * - Hole full: concatenated cards as stored (slot order), e.g. AsKh.
 * - Hole shorthand (both cards known only):
 *   - Pair → 77, AA
 *   - Suited non-pair → AKs (higher rank first)
 *   - Offsuit non-pair → AKo
 * - Board: flop glued (Ah7d2c), streets separated by ` | ` —
 *   e.g. Ah7d2c | Ts | 9c. Omit empty streets.
 *
 * Streets / actions
 * - Street headers: PF / F / T / R
 * - Action line: `Actor: fold|check|call|bet|raise|all-in [size]`
 *
 * No classification tags (Softwise M10d): only the actions the user tapped.
 *
 * Meta (optional; omit when empty):
 * - ante (same unit as blinds), effective stack (bb), pot (bb)
 *
 * Human UI/copy uses localized labels + suit symbols; the Pro block and
 * share-card pro lines call this module so notation stays consistent.
 */

import { formatBlindsNotation, formatStackNotation } from './lineText'
import { formatPotDisplay } from './potLabel'
import type {
  Card,
  HandState,
  Rank,
  Street,
  StreetAction,
  Suit,
} from './types'

/** Local seat list — mirrors poker.getPositions without importing poker (cycle). */
function tablePositions(playerCount: number): string[] {
  const count = Math.min(9, Math.max(2, playerCount))
  if (count === 2) return ['BTN', 'BB']
  const n = Math.max(0, count - 3)
  let mid: string[] = []
  if (n === 1) mid = ['UTG']
  else if (n === 2) mid = ['UTG', 'CO']
  else if (n === 3) mid = ['UTG', 'MP', 'CO']
  else if (n === 4) mid = ['UTG', 'MP', 'HJ', 'CO']
  else if (n === 5) mid = ['UTG', 'UTG+1', 'MP', 'HJ', 'CO']
  else if (n > 5) mid = ['UTG', 'UTG+1', 'MP', 'MP+1', 'HJ', 'CO'].slice(0, n)
  return [...mid, 'BTN', 'SB', 'BB']
}

const RANK_ORDER = 'AKQJT98765432'

const SUIT_SYMBOL: Record<Suit, string> = {
  s: '♠',
  h: '♥',
  d: '♦',
  c: '♣',
}

function parseCard(card: Card): { rank: Rank; suit: Suit } {
  return { rank: card[0] as Rank, suit: card[1] as Suit }
}

function rankIndex(rank: Rank): number {
  return RANK_ORDER.indexOf(rank)
}

/** Pro card: As, Kh, Td */
export function formatCardPro(card: Card): string {
  return card
}

/** Human-readable with suit symbols: A♠ */
export function formatCardPretty(card: Card): string {
  const { rank, suit } = parseCard(card)
  return `${rank}${SUIT_SYMBOL[suit]}`
}

export function formatCardsPretty(cards: (Card | null)[]): string {
  return cards
    .filter(Boolean)
    .map((c) => formatCardPretty(c as Card))
    .join(' ')
}

/** Board with street separators: Ah7d2c | Ts | 9c */
export function formatBoardProSpaced(state: HandState): string {
  const flop = state.board.flop
    .filter(Boolean)
    .map((c) => formatCardPro(c as Card))
    .join('')
  const turn = state.board.turn ? formatCardPro(state.board.turn) : ''
  const river = state.board.river ? formatCardPro(state.board.river) : ''
  return [flop, turn, river].filter(Boolean).join(' | ')
}

/**
 * Hole cards full pro: AsKh
 * Shorthand when both known: 77 / AKs / AKo
 */
export function formatHolePro(hole: [Card | null, Card | null]): {
  full: string
  shorthand: string | null
} {
  const [a, b] = hole
  if (!a && !b) return { full: '', shorthand: null }
  if (a && !b) return { full: formatCardPro(a), shorthand: null }
  if (!a && b) return { full: formatCardPro(b), shorthand: null }
  const c1 = a as Card
  const c2 = b as Card
  const full = `${formatCardPro(c1)}${formatCardPro(c2)}`
  return { full, shorthand: holeShorthand(c1, c2) }
}

export function holeShorthand(c1: Card, c2: Card): string {
  const p1 = parseCard(c1)
  const p2 = parseCard(c2)
  let hi = p1
  let lo = p2
  if (rankIndex(p1.rank) > rankIndex(p2.rank)) {
    hi = p2
    lo = p1
  }
  if (hi.rank === lo.rank) return `${hi.rank}${lo.rank}`
  const suited = hi.suit === lo.suit
  return `${hi.rank}${lo.rank}${suited ? 's' : 'o'}`
}

/**
 * Normalize actor labels so Hero chip / localized hero / Hero(POS) match.
 * Position codes are compared case-insensitively after trim.
 */
export function normalizeActorKey(actor: string): string {
  const raw = actor.trim()
  if (!raw) return ''
  const lower = raw.toLowerCase()
  // Hero, Hero(BTN), hero, 히어로, 히어로(BTN), ヒーロー, …
  if (
    lower === 'hero' ||
    lower.startsWith('hero(') ||
    lower === '히어로' ||
    lower.startsWith('히어로(') ||
    lower === 'ヒーロー' ||
    lower.startsWith('ヒーロー(')
  ) {
    return 'hero'
  }
  return lower
}

export function actorsMatch(
  a: string,
  b: string,
  heroPosition?: string,
): boolean {
  const ka = normalizeActorKey(a)
  const kb = normalizeActorKey(b)
  if (ka === '' || kb === '') return false
  if (ka === kb) return true
  // Hero (localized) ≡ hero seat code (BTN/SB/…) when heroPosition known
  if (!heroPosition) return false
  const hp = heroPosition.trim().toLowerCase()
  if (!hp) return false
  if (ka === 'hero' && kb === hp) return true
  if (kb === 'hero' && ka === hp) return true
  return false
}

const STREET_PRO: Record<Street, string> = {
  preflop: 'PF',
  flop: 'F',
  turn: 'T',
  river: 'R',
}

const ACTION_PRO: Record<StreetAction['type'], string> = {
  fold: 'fold',
  check: 'check',
  call: 'call',
  bet: 'bet',
  raise: 'raise',
  allin: 'all-in',
}

export function formatActionPro(action: StreetAction, heroPosition?: string): string {
  // Softwise M10d: no app-inferred classification tags (RFI, 3-bet, squeeze,
  // c-bet, donk, limp, check-raise). Only what the user tapped is shown.
  const size = action.size ? ` ${action.size}` : ''
  // Softwise M10d: seat name only (the line says `BTN 레이즈`, never `Hero`).
  const actor =
    heroPosition && actorsMatch(action.actor, 'Hero', heroPosition)
      ? heroPosition
      : action.actor.trim()
  return `${actor}: ${ACTION_PRO[action.type]}${size}`
}

/** Softwise M10d: the single blinds notation (lineText). */
export function formatBlindsPro(state: HandState): string {
  return formatBlindsNotation(state)
}

/** Softwise M10d: the single stack notation (lineText) — `100bb`, no ≈ chips. */
export function formatStackPro(state: HandState): string {
  return formatStackNotation(state, 'chips')
}

function formatMetaProExtras(state: HandState): string[] {
  const parts: string[] = []
  if (
    state.effectiveStackBb != null &&
    Number.isFinite(state.effectiveStackBb)
  ) {
    parts.push(`eff ${state.effectiveStackBb}bb`)
  }
  const pot = formatPotDisplay(state.potBb)
  if (pot.combinedPro) parts.push(pot.combinedPro)
  return parts
}

/** Compact Discord/forum block — English poker jargon by design. */
function formatHoleProLine(hole: [Card | null, Card | null]): string {
  const h = formatHolePro(hole)
  if (!h.full) return '?'
  return h.shorthand ? `${h.full} (${h.shorthand})` : h.full
}

export function buildProNotation(state: HandState): string {
  const board = formatBoardProSpaced(state)
  const metaExtras = formatMetaProExtras(state)
  const header = [
    `${state.playerCount}max`,
    formatBlindsPro(state),
    `${state.heroPosition} ${formatStackPro(state)}`,
    ...metaExtras,
  ].join(' | ')

  const lines: string[] = [header]
  const heroHole = formatHoleProLine(state.holeCards)
  const positions = tablePositions(state.playerCount)
  const villainLines: string[] = []
  for (const pos of positions) {
    if (pos === state.heroPosition) continue
    const holes = state.knownHoles?.[pos]
    if (!holes || (!holes[0] && !holes[1])) continue
    villainLines.push(`${pos}: ${formatHoleProLine(holes)}`)
  }
  // Softwise M10e: seat + colon on every hole line (matches action lines).
  lines.push(`${state.heroPosition}: ${heroHole}`)
  lines.push(...villainLines)
  if (board) lines.push(`Board: ${board}`)
  else lines.push('Board: (preflop)')

  if (state.actions.length === 0) {
    lines.push('Actions: —')
  } else {
    let current: Street | null = null
    for (const a of state.actions) {
      if (a.street !== current) {
        if (current !== null) lines.push('')
        current = a.street
        lines.push(`${STREET_PRO[a.street]}:`)
      }
      lines.push(`  ${formatActionPro(a, state.heroPosition)}`)
    }
  }

  return lines.join('\n')
}

